import headerLogo from "@/assets/logoc.webp";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";

import {
  ArrowRight,
  Calculator,
  ChevronDown,
  FileText,
  Globe,
  HelpCircle,
  LayoutDashboard,
  Menu,
  Search,
  Settings,
  Share2,
  Wrench,
  X,
} from "lucide-react";

const navItems = [
  {
    name: "Home",
    path: "/",
  },
  {
    name: "Services",
    path: "/services/",
    dropdown: [
      {
        name: "Website Development",
        path: "/services/website-development/",
        icon: Globe,
      },
      {
        name: "Custom Tool Creation",
        path: "/services/custom-tool-creation/",
        icon: Wrench,
      },
      {
        name: "SEO Services",
        path: "/services/search-engine-optimization-company/",
        icon: Search,
      },
      {
        name: "Social Media Marketing",
        path: "/services/social-media-marketing-agency/",
        icon: Share2,
      },
      {
        name: "Maintenance & Support",
        path: "/services/website-maintenance-support/",
        icon: Settings,
      },
      {
        name: "RDSP Tool for Financial Advisors",
        path: "/services/rdsp-tool-for-financial-advisors/",
        icon: Calculator,
      },
      {
        name: "Custom Dashboard Development",
        path: "/services/custom-dashboard-development/",
        icon: LayoutDashboard,
      },
    ],
  },
  {
    name: "About Us",
    path: "/about/",
  },
  {
    name: "Contact",
    path: "/contact/",
  },
];

const megaMenus = {
  Resources: {
    items: [
      {
        name: "FAQs",
        path: "/resources-faqs/",
        icon: HelpCircle,
        description: "Common questions answered",
      },
      {
        name: "Blog",
        path: "/blog/",
        icon: FileText,
        description: "Insights & articles",
      },
    ],
  },
};

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const mobilePanelRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileOpenSection(null);
    setActiveDropdown(null);
    setActiveMegaMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    const focusTimer = window.setTimeout(() => {
      mobilePanelRef.current?.focus?.();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileMenuOpen]);

  const isActivePath = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`)
    );
  };

  const toggleMobileSection = (name) => {
    setMobileOpenSection((previousSection) =>
      previousSection === name ? null : name
    );
  };

  const handleMobileNavigate = (path) => {
    setMobileMenuOpen(false);
    setMobileOpenSection(null);
    navigate(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[999] bg-white transition-all duration-300 ${
          isScrolled
            ? "shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
            : "shadow-[0_4px_16px_rgba(15,23,42,0.04)]"
        }`}
      >
        <nav className="w-full">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="flex h-[84px] items-center justify-between lg:h-[92px]">
              {/* Logo */}
              <div className="flex min-w-0 shrink-0 items-center pr-3 lg:pr-6">
                <Link to="/" className="flex shrink-0 items-center">
                  <motion.img
                    src={headerLogo}
                    alt="Square Trigger"
                    width="1080"
                    height="540"
                    className="h-[42px] w-auto max-w-[160px] shrink-0 object-contain sm:h-14 sm:max-w-[200px] lg:h-16 lg:max-w-none xl:h-[68px]"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  />
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
                <div className="flex items-center justify-center gap-1 xl:gap-2">
                  {navItems.map((item) => (
                    <div
                      key={item.name}
                      className="relative"
                      onMouseEnter={() => {
                        setActiveMegaMenu(null);

                        if (item.dropdown) {
                          setActiveDropdown(item.name);
                        }
                      }}
                      onMouseLeave={() => {
                        if (item.dropdown) {
                          setActiveDropdown(null);
                        }
                      }}
                    >
                      {item.dropdown ? (
                        <button
                          type="button"
                          onClick={() => navigate(item.path)}
                          className={`inline-flex h-12 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4 text-[15px] font-medium transition-all duration-200 xl:px-5 ${
                            activeDropdown === item.name
                              ? "bg-accent/45 text-primary"
                              : "text-foreground/85 hover:bg-accent/45 hover:text-foreground"
                          }`}
                        >
                          {item.name}

                          <ChevronDown
                            className={`h-4 w-4 transition-transform duration-200 ${
                              activeDropdown === item.name
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                      ) : (
                        <Link
                          to={item.path}
                          className={`inline-flex h-12 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4 text-[15px] font-medium transition-all duration-200 xl:px-5 ${
                            isActivePath(item.path)
                              ? "bg-accent/45 text-primary"
                              : "text-foreground/85 hover:bg-accent/45 hover:text-foreground"
                          }`}
                        >
                          {item.name}
                        </Link>
                      )}

                      <AnimatePresence>
                        {item.dropdown &&
                          activeDropdown === item.name && (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.18 }}
                              className="absolute left-0 top-full z-[1001] mt-3 max-h-[70vh] w-80 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl"
                            >
                              {item.dropdown.map((subItem) => {
                                const Icon = subItem.icon;

                                return (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.path}
                                    className={`group flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-all duration-200 hover:bg-accent ${
                                      isActivePath(subItem.path)
                                        ? "bg-accent text-foreground"
                                        : ""
                                    }`}
                                  >
                                    <div className="rounded-xl bg-accent p-2 transition-colors group-hover:bg-primary/10">
                                      <Icon className="h-4 w-4 text-primary" />
                                    </div>

                                    <span className="font-medium text-foreground">
                                      {subItem.name}
                                    </span>
                                  </Link>
                                );
                              })}
                            </motion.div>
                          )}
                      </AnimatePresence>
                    </div>
                  ))}

                  {Object.keys(megaMenus).map((menuName) => (
                    <div
                      key={menuName}
                      className="relative"
                      onMouseEnter={() => {
                        setActiveDropdown(null);
                        setActiveMegaMenu(menuName);
                      }}
                      onMouseLeave={() => {
                        setActiveMegaMenu(null);
                      }}
                    >
                      <button
                        type="button"
                        className={`inline-flex h-12 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4 text-[15px] font-medium transition-all duration-200 xl:px-5 ${
                          activeMegaMenu === menuName
                            ? "bg-accent/45 text-foreground"
                            : "text-foreground/85 hover:bg-accent/45 hover:text-foreground"
                        }`}
                      >
                        {menuName}

                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            activeMegaMenu === menuName
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </button>

                      <AnimatePresence>
                        {activeMegaMenu === menuName && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.18 }}
                            className="absolute left-1/2 top-full z-[1001] mt-3 w-[440px] -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
                          >
                            <div className="grid grid-cols-1 gap-1">
                              {megaMenus[menuName].items.map(
                                (menuItem) => {
                                  const Icon = menuItem.icon;

                                  return (
                                    <Link
                                      key={menuItem.name}
                                      to={menuItem.path}
                                      className={`group flex items-start gap-3 rounded-xl px-3 py-3 transition-all duration-200 hover:bg-accent ${
                                        isActivePath(menuItem.path)
                                          ? "bg-accent"
                                          : ""
                                      }`}
                                    >
                                      <div className="shrink-0 rounded-xl bg-accent p-2 transition-colors group-hover:bg-primary/10">
                                        <Icon className="h-4 w-4 text-primary" />
                                      </div>

                                      <div>
                                        <span className="block font-medium text-foreground">
                                          {menuItem.name}
                                        </span>

                                        <span className="text-xs text-muted-foreground">
                                          {menuItem.description}
                                        </span>
                                      </div>
                                    </Link>
                                  );
                                }
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop Consultation Button */}
              <div className="hidden shrink-0 items-center pl-3 lg:flex lg:pl-6">
                <Button
                  variant="gradient"
                  asChild
                  className="group h-12 whitespace-nowrap rounded-xl px-6 text-[15px] font-semibold"
                >
                  <Link
                    to="/get-started/"
                    className="inline-flex items-center gap-2"
                  >
                    Book a Free Consultation

                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <div className="flex items-center lg:hidden">
                <button
                  type="button"
                  aria-label={
                    mobileMenuOpen ? "Close menu" : "Open menu"
                  }
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-menu-panel"
                  className="relative z-[2002] inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-foreground transition-colors hover:bg-accent"
                  onClick={() => {
                    setMobileMenuOpen(
                      (previousState) => !previousState
                    );
                  }}
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[1995] bg-black/45 lg:hidden"
              onClick={() => {
                setMobileMenuOpen(false);
              }}
            />

            <motion.aside
              id="mobile-menu-panel"
              ref={mobilePanelRef}
              tabIndex={-1}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{
                type: "tween",
                duration: 0.28,
              }}
              className="fixed right-0 top-0 z-[2000] flex h-[100dvh] w-[88vw] max-w-[390px] flex-col border-l border-slate-200 bg-white shadow-2xl lg:hidden"
            >
              {/* Mobile Header */}
              <div className="flex min-h-[84px] shrink-0 items-center justify-between border-b border-slate-200 px-4">
                <button
                  type="button"
                  onClick={() => {
                    handleMobileNavigate("/");
                  }}
                  className="flex shrink-0 items-center"
                >
                  <img
                    src={headerLogo}
                    alt="Square Trigger"
                    width="1080"
                    height="540"
                    className="h-[44px] w-auto max-w-[170px] shrink-0 object-contain sm:max-w-[190px]"
                  />
                </button>

                <button
                  type="button"
                  aria-label="Close menu"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-foreground transition-colors hover:bg-accent"
                  onClick={() => {
                    setMobileMenuOpen(false);
                  }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Mobile Menu Content */}
              <div className="flex-1 overflow-y-auto px-3 py-4">
                <div className="space-y-2">
                  {navItems.map((item) => {
                    const hasDropdown = Boolean(item.dropdown);

                    if (!hasDropdown) {
                      return (
                        <button
                          key={item.name}
                          type="button"
                          onClick={() => {
                            handleMobileNavigate(item.path);
                          }}
                          className={`flex min-h-[54px] w-full items-center justify-between rounded-2xl px-4 text-[15px] font-medium transition-colors ${
                            isActivePath(item.path)
                              ? "bg-accent text-foreground"
                              : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <span>{item.name}</span>

                          <ArrowRight className="h-4 w-4 opacity-60" />
                        </button>
                      );
                    }

                    return (
                      <div
                        key={item.name}
                        className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                      >
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              handleMobileNavigate(item.path);
                            }}
                            className={`flex min-h-[54px] flex-1 items-center px-4 text-left text-[15px] font-medium transition-colors ${
                              isActivePath(item.path)
                                ? "bg-accent text-foreground"
                                : "text-foreground hover:bg-accent"
                            }`}
                          >
                            <span>{item.name}</span>
                          </button>

                          <button
                            type="button"
                            aria-label={`Toggle ${item.name} menu`}
                            onClick={() => {
                              toggleMobileSection(item.name);
                            }}
                            className="flex h-[54px] w-[54px] items-center justify-center border-l border-slate-200 transition-colors hover:bg-accent"
                          >
                            <ChevronDown
                              className={`h-5 w-5 transition-transform ${
                                mobileOpenSection === item.name
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />
                          </button>
                        </div>

                        <AnimatePresence initial={false}>
                          {mobileOpenSection === item.name && (
                            <motion.div
                              initial={{
                                height: 0,
                                opacity: 0,
                              }}
                              animate={{
                                height: "auto",
                                opacity: 1,
                              }}
                              exit={{
                                height: 0,
                                opacity: 0,
                              }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden border-t border-slate-200 bg-background/40"
                            >
                              <div className="space-y-1 p-2">
                                {item.dropdown.map((subItem) => {
                                  const Icon = subItem.icon;

                                  return (
                                    <button
                                      key={subItem.name}
                                      type="button"
                                      onClick={() => {
                                        handleMobileNavigate(
                                          subItem.path
                                        );
                                      }}
                                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors ${
                                        isActivePath(subItem.path)
                                          ? "bg-accent text-foreground"
                                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                      }`}
                                    >
                                      <span className="shrink-0 rounded-xl bg-accent/70 p-2">
                                        <Icon className="h-4 w-4 text-primary" />
                                      </span>

                                      <span className="font-medium">
                                        {subItem.name}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                {/* Mobile Resources Menu */}
                <div className="mt-4 space-y-2">
                  {Object.entries(megaMenus).map(
                    ([menuName, menu]) => (
                      <div
                        key={menuName}
                        className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            toggleMobileSection(menuName);
                          }}
                          className="flex min-h-[54px] w-full items-center justify-between px-4 text-left text-[15px] font-medium transition-colors hover:bg-accent"
                        >
                          <span className="text-foreground">
                            {menuName}
                          </span>

                          <ChevronDown
                            className={`h-5 w-5 transition-transform ${
                              mobileOpenSection === menuName
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>

                        <AnimatePresence initial={false}>
                          {mobileOpenSection === menuName && (
                            <motion.div
                              initial={{
                                height: 0,
                                opacity: 0,
                              }}
                              animate={{
                                height: "auto",
                                opacity: 1,
                              }}
                              exit={{
                                height: 0,
                                opacity: 0,
                              }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden border-t border-slate-200 bg-background/40"
                            >
                              <div className="space-y-1 p-2">
                                {menu.items.map((menuItem) => {
                                  const Icon = menuItem.icon;

                                  return (
                                    <button
                                      key={menuItem.name}
                                      type="button"
                                      onClick={() => {
                                        handleMobileNavigate(
                                          menuItem.path
                                        );
                                      }}
                                      className={`flex w-full items-start gap-3 rounded-xl px-3 py-3 text-sm transition-colors ${
                                        isActivePath(menuItem.path)
                                          ? "bg-accent text-foreground"
                                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                      }`}
                                    >
                                      <span className="mt-0.5 shrink-0 rounded-xl bg-accent/70 p-2">
                                        <Icon className="h-4 w-4 text-primary" />
                                      </span>

                                      <span className="text-left leading-tight">
                                        <span className="block font-medium text-foreground">
                                          {menuItem.name}
                                        </span>

                                        <span className="text-xs text-muted-foreground">
                                          {menuItem.description}
                                        </span>
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )
                  )}
                </div>

                {/* Mobile Consultation Button */}
                <div className="mt-5 space-y-3 border-t border-slate-200 pb-6 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      handleMobileNavigate("/get-started/");
                    }}
                    className="h-12 w-full rounded-xl bg-gradient-to-r from-purple-600 to-sky-500 text-[15px] font-semibold text-white transition-opacity hover:opacity-95"
                  >
                    Book a Free Consultation
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
